import { ArrowRight, Brain, MessagesSquare, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { RegistrationCaptcha } from "@/components/RegistrationCaptcha";
import { BrandMark } from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { hasToken, useAuth } from "@/hooks/useAuth";
import { isInviteCode, normalizeInviteInput } from "@/lib/invites";

import { loginDestination } from "@/lib/cabinetNavigation";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTermsVersion, setAcceptedTermsVersion] = useState<string | null>(null);
  const [adultConfirmed, setAdultConfirmed] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const [messengerRegistrationToken] = useState(() =>
    new URLSearchParams(window.location.hash.slice(1)).get("messenger") ?? undefined
  );
  const referralCode = params.get("ref") ?? undefined;
  const initialInvite = normalizeInviteInput(params.get("invite") ?? "");
  // Only a fixed in-app destination is accepted; never redirect to an arbitrary URL.
  const next = params.get("next");
  const destination = loginDestination(next, location.state?.from);
  const policy = useQuery({
    queryKey: ["registration-policy"],
    queryFn: async () => {
      const result = await api.get<{ inviteRequired: boolean; captchaRequired?: boolean; captchaSiteKey?: string; termsVersion: string; termsUrl: string }>("/v1/auth/registration-policy");
      if (!result.ok) throw new Error(result.error);
      return result.data;
    },
    staleTime: 0,
  });
  const inviteRequired = policy.data?.inviteRequired !== false && !referralCode;
  // Accept only same-origin versioned document paths supplied by the backend.
  const termsUrl = policy.data?.termsUrl;
  const legalReady = Boolean(policy.data?.termsVersion && termsUrl?.startsWith("/public/legal/") && !termsUrl.includes("\\"));
  // A loaded policy without a usable agreement is a failure, not "still loading".
  const legalFailed = policy.isError || (policy.isSuccess && !legalReady);
  const termsAccepted = legalReady && acceptedTermsVersion === policy.data?.termsVersion;
  const canRegister = legalReady && termsAccepted && adultConfirmed;
  const [inviteCode, setInviteCode] = useState(initialInvite);
  const [inviteError, setInviteError] = useState("");
  const [mode, setMode] = useState<"login" | "register">(
    initialInvite || referralCode || params.get("mode") === "register" ? "register" : "login"
  );
  const [pending, setPending] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaAttempt, setCaptchaAttempt] = useState(0);
  const captchaRequired = policy.data?.captchaRequired === true;
  const captchaReady = !captchaRequired || Boolean(captchaToken);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password || (mode === "register" && inviteRequired && !inviteCode.trim())) return;
    if (mode === "register" && !canRegister) return;
    if (mode === "register" && !captchaReady) return;
    const normalizedInvite = normalizeInviteInput(inviteCode);
    if (mode === "register" && (inviteRequired || normalizedInvite) && !isInviteCode(normalizedInvite)) {
      setInviteError(
        "Это не код приглашения. Вставьте код, начинающийся с invite_, или всю пригласительную ссылку."
      );
      return;
    }
    setPending(true);
    const res =
      mode === "login"
        ? await auth.login(email, password)
        : await auth.register(email, password, normalizedInvite, {
            termsAccepted,
            termsVersion: policy.data!.termsVersion,
            adultConfirmed,
            captchaToken,
            messengerRegistrationToken,
            referralCode,
          });
    setPending(false);
    if (mode === "register") { setCaptchaToken(""); setCaptchaAttempt(n => n + 1); }
    if (!res.ok) {
      if (mode === "register") void policy.refetch();
      toast.error(mode === "login" ? "Не удалось войти" : "Не удалось зарегистрироваться", {
        description: res.error,
      });
      return;
    }
    const messengerLinked = "messengerLinked" in res && res.messengerLinked === true;
    toast.success(mode === "login" ? "С возвращением" : messengerLinked
      ? "Telegram подключён — возвращайся в бота и пиши. Начислено 50 пробных кредитов"
      : "Регистрация завершена — начислено 50 пробных кредитов", { duration: 10000 });
    window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
    navigate(destination, { replace: true });
  }

  // Already signed in: "Войти" on the landing page goes straight to the cabinet.
  if (hasToken()) {
    return <Navigate to={destination} replace />;
  }

  return (
    <div className="grid min-h-dvh bg-background lg:grid-cols-[1.05fr_0.95fr]">
      <section className="relative hidden overflow-hidden bg-brand-forest p-10 text-white lg:flex lg:flex-col xl:p-14">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-brand-violet/20 blur-3xl" />
        <div className="absolute -bottom-40 -left-24 h-96 w-96 rounded-full bg-brand-coral/10 blur-3xl" />

        <a href="/" className="relative flex w-fit items-center gap-3" aria-label="MyAiFriend — на главную">
          <BrandMark />
          <span className="text-lg font-extrabold tracking-[-0.04em]">MyAiFriend</span>
        </a>

        <div className="relative my-auto max-w-2xl py-14">
          <p className="mb-5 text-xs font-bold uppercase tracking-[0.16em] text-brand-lime">
            Личный ИИ с долгосрочной памятью
          </p>
          <h1 className="text-5xl font-semibold leading-[1.05] tracking-[-0.055em] xl:text-6xl">
            Разговор продолжается. Даже если прошло много времени.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-7 text-white/58 xl:text-lg">
            MyAiFriend помнит важное, сохраняет ваш контекст между web и Telegram и
            подстраивает характер общения под вас.
          </p>

          <div className="mt-10 grid max-w-xl gap-3 sm:grid-cols-3">
            <Benefit icon={Brain} text="Долгая память" />
            <Benefit icon={MessagesSquare} text="Web + Telegram" />
            <Benefit icon={ShieldCheck} text="Всё под контролем" />
          </div>
        </div>

        <p className="relative text-xs text-white/32">© 2026 MyAiFriend</p>
      </section>

      <main className="flex items-center justify-center px-4 py-10 sm:px-8 lg:px-12">
        <div className="w-full max-w-md">
          <a href="/" className="mb-10 flex w-fit items-center gap-3 lg:hidden">
            <BrandMark />
            <span className="font-extrabold tracking-[-0.04em]">MyAiFriend</span>
          </a>

          <div className="mb-7">
            <p className="page-kicker">{mode === "login" ? "С возвращением" : "Начнём знакомство"}</p>
            <h2 className="mt-2 text-4xl font-semibold tracking-[-0.05em]">
              {mode === "login" ? "Войдите в кабинет" : "Создайте аккаунт"}
            </h2>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              {mode === "login"
                ? "Продолжите диалог с того места, где остановились."
                : inviteRequired
                  ? "Регистрация по приглашению. Введите код, e-mail и пароль."
                  : "Укажите e-mail и придумайте пароль. Затем создайте своего персонажа."}
            </p>
          </div>

          <Card className="border-border/70 bg-card/85 shadow-[0_24px_80px_rgba(16,35,28,0.1)]">
            <CardHeader className="pb-4">
              <CardTitle className="text-base">Данные для входа</CardTitle>
              <CardDescription>Ваши диалоги доступны только после авторизации.</CardDescription>
            </CardHeader>
            <CardContent>
              {mode === "register" && referralCode && <p className="mb-4 rounded-xl border border-primary/30 bg-primary/10 p-4 text-sm font-semibold">Вас пригласил друг! Зарегистрируйтесь по этой ссылке и получите +10% кредитов к первому оплаченному пакету и скидку 10% на первую подписку.</p>}
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Пароль</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                {mode === "register" && (inviteRequired || Boolean(initialInvite) || Boolean(inviteCode)) && (
                  <div className="space-y-1.5">
                    <Label htmlFor="inviteCode">Код приглашения{!inviteRequired && " (необязательно)"}</Label>
                    <Input
                      id="inviteCode"
                      autoComplete="one-time-code"
                      value={inviteCode}
                      onChange={(e) => {
                        setInviteCode(e.target.value);
                        setInviteError("");
                      }}
                      onBlur={() => setInviteCode(normalizeInviteInput(inviteCode))}
                      placeholder="invite_… или пригласительная ссылка"
                      aria-invalid={Boolean(inviteError)}
                      required={inviteRequired}
                    />
                    <p className={inviteError ? "text-xs text-destructive" : "text-xs text-muted-foreground"}>
                      {inviteError ||
                        "Метка приглашения не подходит. Нужен код invite_… или целая ссылка от владельца сервиса."}
                    </p>
                  </div>
                )}
                {mode === "register" && (
                  <div className="space-y-3 border-t border-border pt-4 text-sm leading-5">
                    {legalReady ? (
                      <label className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 shrink-0 accent-brand-forest"
                          checked={termsAccepted}
                          required
                          onChange={(e) => setAcceptedTermsVersion(e.target.checked ? policy.data!.termsVersion : null)}
                        />
                        <span>Я принимаю <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">пользовательское соглашение</a></span>
                      </label>
                    ) : (
                      <p role="status" className="text-muted-foreground">
                        {legalFailed ? "Не удалось загрузить условия регистрации." : "Загружаем условия регистрации…"}
                        {legalFailed && <button type="button" className="ml-1 underline" onClick={() => void policy.refetch()}>Повторить</button>}
                      </p>
                    )}
                    <label className="flex items-start gap-3">
                      <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-brand-forest" checked={adultConfirmed} required onChange={(e) => setAdultConfirmed(e.target.checked)} />
                      <span>Мне исполнилось 18 лет</span>
                    </label>
                    <p className="text-xs text-muted-foreground">Создание аккаунта не оформляет подписку и не списывает деньги.</p>
                  </div>
                )}
                {mode === "register" && <p className="text-sm text-muted-foreground">50 пробных кредитов после регистрации. Число сообщений зависит от выбранной модели.</p>}
                {mode === "register" && captchaRequired && <RegistrationCaptcha siteKey={policy.data?.captchaSiteKey ?? ""} onToken={setCaptchaToken} attempt={captchaAttempt} />}
                <Button type="submit" size="lg" className="mt-2 w-full" disabled={pending || (mode === "register" && (!canRegister || !captchaReady))}>
                  {pending
                    ? "Подождите…"
                    : mode === "login"
                      ? "Войти в кабинет"
                      : "Создать аккаунт"}
                  {!pending && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
                <div className="text-center text-sm text-muted-foreground">
                  {mode === "login" ? (
                    <>
                      Нет аккаунта?{" "}
                      <button
                        type="button"
                        className="font-semibold text-foreground underline decoration-brand-coral/50 underline-offset-4 hover:decoration-brand-coral"
                        onClick={() => setMode("register")}
                      >
                        Зарегистрироваться
                      </button>
                    </>
                  ) : (
                    <>
                      Уже есть аккаунт?{" "}
                      <button
                        type="button"
                        className="font-semibold text-foreground underline decoration-brand-coral/50 underline-offset-4 hover:decoration-brand-coral"
                        onClick={() => setMode("login")}
                      >
                        Войти
                      </button>
                    </>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}

function Benefit({
  icon: Icon,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.055] p-4">
      <Icon className="h-5 w-5 text-brand-lime" />
      <p className="mt-3 text-xs font-semibold text-white/72">{text}</p>
    </div>
  );
}
